/**
 * BehaviorGraph Editor Panel - Pop-out Window
 *
 * Opens a ReactFlow-based node graph editor for BehaviorGraph behaviors.
 * Follows the same popup window pattern as InteractionsFlowPanel.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ReactFlowProvider } from 'reactflow';
import type { BehaviorGraphData, GraphData } from 'trace-viewer/behaviors';
import type { TraceBehavior, TraceViewerElement } from 'trace-viewer';
import { BehaviorGraph, raiseBehaviorGraphData } from 'trace-viewer/behaviors';
import { EDITOR_EVENTS, dispatchBehaviorChanged, type BehaviorChangedDetail } from '../editor-events';
import { BehaviorGraphEditor } from './BehaviorGraphEditor';
import { REACTFLOW_CSS } from '../interactions-editor-reactflow/reactflow-styles';

// ============================================================================
// Popup Window Manager
// ============================================================================

let popupWindow: Window | null = null;
let popupRoot: Root | null = null;

/**
 * Opens the BehaviorGraph Editor in a popup window.
 */
export function openBehaviorGraphPopup(): void {
    if (popupWindow && !popupWindow.closed) {
        popupWindow.focus();
        return;
    }

    popupWindow = window.open(
        '',
        'behaviorgraph-editor',
        'width=1100,height=750,menubar=no,toolbar=no,location=no,status=no'
    );

    if (!popupWindow) {
        console.error('[BehaviorGraph Popup] Failed to open - check popup blocker');
        return;
    }

    popupWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Behavior Graph Editor</title>
            <style>
                ${REACTFLOW_CSS}
                * { box-sizing: border-box; }
                body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                    font-size: 14px;
                    line-height: 1.4;
                    -webkit-font-smoothing: antialiased;
                    margin: 0;
                    padding: 0;
                    background: #1e1e1e;
                    color: #ccc;
                    overflow: hidden;
                }
                #root { width: 100vw; height: 100vh; }
                .react-flow__edge.selected .react-flow__edge-path,
                .react-flow__edge:focus .react-flow__edge-path,
                .react-flow__edge:focus-visible .react-flow__edge-path {
                    stroke: #58a6ff !important;
                    stroke-width: 3px !important;
                    filter: drop-shadow(0 0 4px rgba(88, 166, 255, 0.5));
                }
                .react-flow__edge.selected, .react-flow__edge:focus, .react-flow__edge:focus-visible {
                    outline: none;
                }
            </style>
        </head>
        <body>
            <div id="root"></div>
        </body>
        </html>
    `);
    popupWindow.document.close();

    const rootEl = popupWindow.document.getElementById('root');
    if (!rootEl) return;

    popupRoot = createRoot(rootEl);
    popupRoot.render(
        <ReactFlowProvider>
            <BehaviorGraphPanelInner />
        </ReactFlowProvider>
    );

    popupWindow.addEventListener('beforeunload', () => {
        popupRoot?.unmount();
        popupRoot = null;
        popupWindow = null;
    });
}

// ============================================================================
// Panel Inner Component
// ============================================================================

const EMPTY_GRAPH: GraphData = { types: [], declarations: [], variables: [], events: [], nodes: [] };

function getViewerElement(): TraceViewerElement | null {
    return document.querySelector('trace-viewer');
}

function BehaviorGraphPanelInner(): React.JSX.Element {
    const [behavior, setBehavior] = useState<TraceBehavior | null>(null);
    const [graphData, setGraphData] = useState<GraphData | null>(null);
    const [allGraphs, setAllGraphs] = useState<GraphData[] | undefined>(undefined);

    useEffect(() => {
        function updateFromSelection(): void {
            const viewer = getViewerElement();
            if (!viewer) return;
            const traceScene = viewer.traceScene;
            if (!traceScene) return;
            const selected = traceScene.getSelectedObject?.() ?? null;
            if (!selected) {
                setBehavior(null);
                setGraphData(null);
                setAllGraphs(undefined);
                return;
            }
            const bg = selected.getBehavior(BehaviorGraph);
            setBehavior(bg);
            if (bg) {
                // The editor works on the RAISED form: every macro expansion is
                // one `macro/…` block, and its private events/variables are not
                // in the editor's state. Raise here, once, so nodes, variables,
                // events and subgraph interfaces all agree on indices. Saving
                // commits the raised form; BehaviorGraph lowers it at ingestion.
                const bgData = raiseBehaviorGraphData(bg.getData());
                const graphs = bgData.graphs;
                if (graphs && graphs.length > 0) {
                    const activeIdx = bgData.activeGraph ?? 0;
                    setGraphData(graphs[activeIdx] ?? EMPTY_GRAPH);
                    setAllGraphs(graphs);
                } else {
                    setGraphData(bgData.graph ?? EMPTY_GRAPH);
                    setAllGraphs(undefined);
                }
            } else {
                setGraphData(null);
                setAllGraphs(undefined);
            }
        }

        updateFromSelection();

        const handler = (): void => updateFromSelection();
        // Only reload graph data when the BehaviorGraph itself changes,
        // not when unrelated behaviors (e.g. Animation) are added/removed
        const behaviorChangedHandler = (event: Event): void => {
            const detail = (event as CustomEvent<BehaviorChangedDetail>).detail;
            if (detail.behaviorType === 'BehaviorGraph') {
                updateFromSelection();
            }
        };
        window.addEventListener('trace-viewer:selection-changed', handler);
        window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, behaviorChangedHandler);
        const opener = window.opener as Window | null;
        if (opener) {
            try {
                opener.addEventListener('trace-viewer:selection-changed', handler);
                opener.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, behaviorChangedHandler);
            } catch (_e) { /* opener closed */ }
        }
        return () => {
            window.removeEventListener('trace-viewer:selection-changed', handler);
            window.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, behaviorChangedHandler);
            if (opener) {
                try {
                    opener.removeEventListener('trace-viewer:selection-changed', handler);
                    opener.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, behaviorChangedHandler);
                } catch (_e) { /* opener closed */ }
            }
        };
    }, []);

    const handleSave = useCallback((newGraph: GraphData, newAllGraphs?: GraphData[]) => {
        if (!behavior) return;

        // Commit: if multi-graph, use graphs[] mode; otherwise single graph
        const patch: Partial<BehaviorGraphData> = newAllGraphs
            ? { graphs: newAllGraphs, activeGraph: 0 }
            : { graph: newGraph };

        void behavior.commit(patch).then(() => {
            setGraphData(newGraph);
            setAllGraphs(newAllGraphs);
            dispatchBehaviorChanged({
                objectPath: behavior.sceneObject.name,
                behaviorType: 'BehaviorGraph',
                action: 'updated',
            });
        });
    }, [behavior]);

    if (!behavior || !graphData) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888' }}>
                Select an object with a BehaviorGraph behavior
            </div>
        );
    }

    return (
        <BehaviorGraphEditor
            graphData={graphData}
            onSave={handleSave}
            contextObject={behavior.sceneObject}
            allGraphs={allGraphs}
        />
    );
}
